<template>
  <div class="est-room__card est-room__ready">
    <!-- Randevu fişi: kiminle, hangi hizmet, ne zaman. Katılımcı doğru yerde ve
         saatte olduğunu buradan doğrular; oda sahibi katılımcıyı görür. Üst etiket
         Vega sözlüğüyle: karşı taraf "Katılımcı" (`Attender`), host "Oda sahibi";
         katılımcı her zaman danışan değildir (`isGuestCustomer`). -->
    <meeting-ticket-component :name="counterpartName"
                              :eyebrow="$t(isHost ? 'OnlineMeetingComponent.Attender' : 'OnlineMeetingComponent.VegaRoomHost')"
                              :info="meetingInfo"
                              :connection="connection" />

    <!-- Bekleme bir ilerlemedir: hangi adımda olduğun rayda görünür. -->
    <meeting-steps-component :steps="steps" />

    <!-- Şimdi ne oluyor / ne yapmalı. Dar ekranda sayfanın altına sabitlenir
         (CSS), böylece "Katıl" hiç gözden kaçmaz. -->
    <div ref="next" class="est-room__next" :class="[next.tone, { 'is-action': next.kind === 'action' }]" role="status" aria-live="polite">
      <template v-if="next.kind === 'action'">
        <p class="est-room__next-text">{{ $t(next.text) }}</p>

        <button type="button" class="btn btn-primary est-room__cta" @click="act">
          <font-awesome-icon icon="fa-solid fa-video" class="mr-1" /> {{ $t(next.cta) }}
        </button>
      </template>

      <template v-else>
        <span class="est-room__next-icon" aria-hidden="true"><font-awesome-icon :icon="next.icon" :spin="next.spin === true" /></span>
        <div class="est-room__next-body">
          <p class="est-room__next-title">{{ $t(next.title) }}</p>
          <p v-if="next.text" class="est-room__next-text">{{ $t(next.text) }}</p>
        </div>
      </template>
    </div>

    <!-- Cihazlar: iki kutu yan yana, her biri kendi durumunu taşır. Açıklama ve
         kurtarma aksiyonu yalnız sorun varken açılır. -->
    <div class="est-room__devices">
      <ul class="est-room__tiles">
        <li v-for="check in checks" :key="check.key" class="est-room__tile" :class="check.tone">
          <span class="est-room__tile-icon" aria-hidden="true"><font-awesome-icon :icon="check.icon" /></span>
          <span class="est-room__tile-body">
            <span class="est-room__tile-name">{{ $t(check.name) }}</span>
            <span class="est-room__tile-state">{{ $t(check.state) }}</span>
          </span>
        </li>
      </ul>

      <template v-if="!mediaReady">
        <p class="est-room__note-text">{{ $t(mediaState.text) }}</p>
        <button v-if="mediaState.canRetry" type="button" class="btn btn-outline-secondary btn-sm est-room__note-action" @click="$emit('retry-media')">
          <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> {{ $t('TryAgain') }}
        </button>
      </template>
    </div>

    <!-- Kartın ayağı: davet bağlantısı (host, danışan gelmeden) ya da ortam ipucu
         (danışan). Kart sahne yüksekliğine uzar, ayak alta oturur. -->
    <div class="est-room__ready-foot">
      <meeting-invitation-component v-if="isHost && !connections.SignalR" :meeting-url="meetingUrl" />

      <p v-if="!isHost" class="est-room__fine">
        <font-awesome-icon icon="fa-solid fa-lightbulb" />
        <span>{{ $t('OnlineMeetingComponent.MeetingTip') }}</span>
      </p>
    </div>
  </div>
</template>

<script>
  import MeetingInvitationComponent from './MeetingInvitationComponent.vue'
  import MeetingStepsComponent from './MeetingStepsComponent.vue'
  import MeetingTicketComponent from './MeetingTicketComponent.vue'
  import { playNotificationSound } from '../../../helpers/notificationSound'

  // Hazırlık kartı. Ekranın tek sorusu "şimdi ne oluyor, ben ne yapmalıyım";
  // cevap `next` bloğudur ve tek birincil aksiyonu (Katıl / Başlat) o taşır.
  // Önizleme sahnesi ayrı bileşendir (`MeetingPreviewComponent`).
  //
  // İki bağlantı bayrağı ayrı okunur: `connections.Hub` odaya bağlı olmak,
  // `connections.SignalR` karşı tarafın odada olması. Eski hâl ikisini tek bayrakta
  // tutuyordu ve uzman henüz gelmemişken danışan sonsuza kadar "Bağlanıyor" görüyordu.
  export default {
    name: 'MeetingLandingComponent',
    components: { MeetingInvitationComponent, MeetingStepsComponent, MeetingTicketComponent },
    props: {
      connections: {
        type: Object,
        required: true
      },
      mediaState: {
        type: Object,
        required: true
      },
      meetingInfo: {
        type: Object,
        default: null
      },
      counterpartName: {
        type: String,
        default: ''
      },
      connection: {
        type: Object,
        default: null
      },
      isHost: Boolean,
      meetingUrl: String,
      isCameraOpen: Boolean,
      isMicrophoneOpen: Boolean,
      isJoining: Boolean
    },
    methods: {
      act() {
        this.$emit(this.isHost ? 'start-meeting' : 'join-meeting');
      },
      // Uzman görüşmeyi başlattığında danışan telefonu elinden bırakmış olabilir:
      // ses + titreşim + bloğu görünür kıl. Hiçbiri akışı durdurmaz.
      announceReady() {
        playNotificationSound();

        // Tarayıcı, sayfaya hiç dokunulmamışken titreşimi engeller ve konsola
        // "Blocked call to navigator.vibrate" düşürür; try/catch bunu yakalamaz,
        // çünkü çağrı hata atmadan yok sayılır. `userActivation` bilmeyen
        // tarayıcıda (Safari) eski davranış korunur.
        const activation = navigator.userActivation;

        if (navigator.vibrate && (!activation || activation.hasBeenActive)) {
          try {
            navigator.vibrate([150, 80, 150]);
          } catch (error) {
            // Titreşim desteklenmiyorsa sessizce geçilir.
          }
        }

        this.$nextTick(() => {
          const el = this.$refs.next;
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }
        });
      },
      deviceCheck(key, name, icon, offIcon, isOpen) {
        switch (this.connections.LocalCamera) {
          case 1:
            return isOpen
              ? { key, name, icon, tone: 'is-ok', state: 'OnlineMeetingComponent.StateReady' }
              : { key, name, icon: offIcon, tone: 'is-muted', state: 'OnlineMeetingComponent.StateOff' };
          case -1:
            return { key, name, icon: offIcon, tone: 'is-danger', state: 'OnlineMeetingComponent.StateBlocked' };
          case -2:
            return { key, name, icon: offIcon, tone: 'is-danger', state: 'OnlineMeetingComponent.StateNotFound' };
          case -3:
            return { key, name, icon: offIcon, tone: 'is-danger', state: 'OnlineMeetingComponent.StateUnsupported' };
          default:
            return { key, name, icon, tone: 'is-wait', state: 'OnlineMeetingComponent.StateChecking' };
        }
      }
    },
    computed: {
      mediaReady() {
        return this.connections.LocalCamera === 1;
      },
      isMeetingStarted() {
        return this.$store.getters['Meeting/isMeetingStarted'] === true;
      },
      // Raydaki "şimdiki adım" bitmemiş ilk adımdır; sonrakiler bekler. Görüşme
      // adımı hazırlıkta hiç `done` olmaz — o hâl bu kartın kendisini kaldırır.
      steps() {
        const devices = this.mediaReady ? 'done' : (this.connections.LocalCamera === 0 ? 'active' : 'danger');
        const other = this.connections.SignalR ? 'done' : (devices === 'done' ? 'active' : 'pending');
        const call = devices === 'done' && other === 'done' ? 'active' : 'pending';

        return [
          { key: 'devices', label: this.$t('OnlineMeetingComponent.StepDevices'), state: devices },
          { key: 'other', label: this.$t(this.isHost ? 'OnlineMeetingComponent.Attender' : 'OnlineMeetingComponent.VegaRoomHost'), state: other },
          { key: 'call', label: this.$t('OnlineMeetingComponent.StepCall'), state: call }
        ];
      },
      // Tek doğruluk kaynağı: bloğun hâli. `action` yalnız bir kişide ve bir anda
      // vardır — host için "Başlat", danışan için "Katıl".
      next() {
        if (!this.mediaReady) {
          const pending = this.connections.LocalCamera === 0;
          return {
            kind: 'info',
            tone: pending ? 'is-wait' : 'is-danger',
            icon: pending ? 'fa-solid fa-circle-notch' : this.mediaState.icon,
            spin: pending,
            title: this.mediaState.title,
            text: pending ? this.mediaState.text : 'OnlineMeetingComponent.FixDevicesFirst'
          };
        }

        if (!this.connections.Hub) {
          return { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.StateConnecting', text: null };
        }

        if (!this.connections.SignalR) {
          return this.isHost
            ? { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.VegaWaitingParticipant', text: 'OnlineMeetingComponent.VegaWaitingParticipantInRoom' }
            : { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.VegaWaitingRoomHost', text: 'OnlineMeetingComponent.VegaWaitingRoomHostForStart' };
        }

        if (this.isHost) {
          return this.isMeetingStarted
            ? { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.VegaWaitingParticipantForAccept', text: null }
            : { kind: 'action', tone: '', text: 'OnlineMeetingComponent.YouCanStartMeeting', cta: 'OnlineMeetingComponent.StartMeeting' };
        }

        if (!this.isMeetingStarted) {
          return { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.VegaRoomHostInRoom', text: 'OnlineMeetingComponent.VegaWaitingRoomHostForStart' };
        }

        if (this.isJoining) {
          return { kind: 'info', tone: 'is-wait', icon: 'fa-solid fa-circle-notch', spin: true, title: 'OnlineMeetingComponent.RedirectToMeeting', text: null };
        }

        return { kind: 'action', tone: '', text: 'OnlineMeetingComponent.VegaRoomHostStarted', cta: 'OnlineMeetingComponent.JoinMeetingLong' };
      },
      checks() {
        return [
          this.deviceCheck('camera', 'OnlineMeetingComponent.DeviceCamera', 'fa-solid fa-video', 'fa-solid fa-video-slash', this.isCameraOpen),
          this.deviceCheck('mic', 'OnlineMeetingComponent.DeviceMicrophone', 'fa-solid fa-microphone', 'fa-solid fa-microphone-slash', this.isMicrophoneOpen)
        ];
      }
    },
    watch: {
      isMeetingStarted(val) {
        if (val && !this.isHost) {
          this.announceReady();
        }
      }
    }
  }
</script>
